import Image from 'next/image'
import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'

import ENV_VARS from '@/config'
import { breakpoints, colors, fontSizes } from '@/styles'
import Head from '@/components/head'

import heroBanner from './uooipropertymanagement-banner.jpg'

const { BASE_URL } = ENV_VARS

const BannerWrapper = styled.div`
  text-align: center;
  margin-bottom: 30px;
  height: 512px;
  overflow: hidden;
  position: relative;

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    height: auto;
  }
`

const Vignette = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background: rgba(20, 20, 20, 0.35);
`

const Container = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  height: 100%;
  width: 100%;
  padding: 0 20px;
  display: flex;
  justify-content: center;
  align-items: center;

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    position: static;
    transform: translate(0, 0);
    padding-left: 25px;
    padding-right: 25px;
    padding-top: 60px;
    padding-bottom: 60px;
  }
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1;
`

const CompanyName = styled.h1`
  color: ${colors.white};
  font-size: 48px;
  line-height: 65px;
  letter-spacing: 0.62px;
  margin: 0 0 45px;
  font-weight: 400;
  text-shadow: 1px 2px 3px rgba(0, 0, 0, 0.4);

  @media screen and (max-width: 930px) {
    font-size: 40px;
    line-height: 57px;
    letter-spacing: 0.55px;
  }

  @media screen and (max-width: 670px) {
    font-size: 36px;
  }

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 22px;
    line-height: 30px;
    letter-spacing: 0.29px;
  }
`

const HorizontalRule = styled.div`
  width: 380px;
  margin-bottom: 40px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.6);

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    width: 220px;
  }
`

const Description = styled.p`
  color: ${colors.white};
  font-size: 17px;
  line-height: 24px;
  font-weight: 400;
  letter-spacing: 0.23px;
  margin-bottom: 50px;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);
  max-width: 890px;

  small {
    font-size: ${fontSizes.small};
  }

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    display: none;
  }
`

const DescriptionMobile = styled.p`
  color: ${colors.white};
  font-size: ${fontSizes.small};
  line-height: 22px;
  font-weight: 400;
  letter-spacing: 0.23px;
  margin-bottom: 50px;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);

  @media only screen and (min-width: ${breakpoints.tabletPortrait}) {
    display: none;
  }
`

const ContactInfo = styled.p`
  color: ${colors.white};
  font-size: 22px;
  line-height: 30px;
  font-weight: 400;
  letter-spacing: 0.23px;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    display: none;
  }
`

const ContactInfoMobile = styled.p`
  color: ${colors.white};
  font-size: 18px;
  line-height: 24px;
  font-weight: 400;
  letter-spacing: 0.23px;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);

  @media only screen and (min-width: ${breakpoints.tabletPortrait}) {
    display: none;
  }
`

const SectionTitle = styled.h3`
  color: ${colors.regular};
  font-size: ${fontSizes.medium};
  font-weight: bold;
  letter-spacing: -0.49px;
  line-height: 22px;
  text-align: center;
  margin-bottom: 30px;

  @media screen and (max-width: 700px) {
    display: none;
  }
`

const PlainLink = styled.a`
  color: inherit;
  text-decoration: none;
  cursor: pointer;
`

const UooiPropertyManagementProfile = ({ metadata }) => {
  const { t } = useTranslation('uooipropertymanagement')

  const profile = {
    banner: heroBanner.src,
    title: t('uooipropertymanagement:Uooi Property Management'),
    pageDescription: t('uooipropertymanagement:description.meta'),
    url: '/company/uooipropertymanagement'
  }

  return (
    <>
      <Head
        title={`${profile.title} | liv.rent`}
        description={profile.pageDescription}
        meta={{
          image: `${BASE_URL}/images/uooipropertymanagement-banner.jpg`
        }}
        path={profile.url}
        pageData={metadata}
        hideCustomerSupport
      />
      <BannerWrapper>
        <Image
          fill
          priority
          src={profile.banner}
          alt="Uooi Property Management"
          sizes="(max-width: 768px) 768px, (max-width: 1200px) 1200px"
        />
        <Container>
          <Vignette />
          <ContentContainer>
            <CompanyName>{profile.title}</CompanyName>
            <HorizontalRule />
            <Trans i18nKey="uooipropertymanagement:description.desktop">
              <Description>
                We are a specialized rental management firm that recognize the
                uniqueness of every property and we design customized solutions
                meet the needs of each client. #RealEstateManagement
                #PropertyInvestment #GlobalPropertyManagement
                #DedicatedRentalManagement
                <br />
                <br />
                Professional Portfolio Management | Trusted Tenant Selection |
                Reliable Client Relationship
                <br />
                <small>Licensed Rental Management Services</small>
              </Description>
            </Trans>
            <Trans i18nKey="uooipropertymanagement:contact.desktop">
              <ContactInfo>
                <PlainLink href="tel:1-866-496-7548">1-866-496-7548</PlainLink>
                {' (1-UOOI-WORK-4-U) or '}
                <PlainLink href="tel:1-604-655-5708">1-604-655-5708</PlainLink>
                {' | '}
                <PlainLink href="mailto:info@uooi.com">info@uooi.com</PlainLink>
              </ContactInfo>
            </Trans>
            <Trans i18nKey="uooipropertymanagement:description.mobile">
              <DescriptionMobile>
                We are a specialized rental management firm that recognize the
                uniqueness of every property and we design customized solutions
                meet the needs of each client. #RealEstateManagement
                #PropertyInvestment #GlobalPropertyManagement
                #DedicatedRentalManagement
                <br />
                <br />
                Professional Portfolio Management
                <br />
                Trusted Tenant Selection
                <br />
                Reliable Client Relationship
                <br />
                <small>Licensed Rental Management Services</small>
              </DescriptionMobile>
            </Trans>
            <Trans i18nKey="uooipropertymanagement:contact.mobile">
              <ContactInfoMobile>
                {'Call '}
                <PlainLink href="tel:1-866-496-7548">1-866-496-7548</PlainLink>
                {' (1-UOOI-WORK-4-U) or '}
                <PlainLink href="tel:1-604-655-5708">1-604-655-5708</PlainLink>
                <br />
                <PlainLink href="mailto:info@uooi.com">info@uooi.com</PlainLink>
              </ContactInfoMobile>
            </Trans>
          </ContentContainer>
        </Container>
      </BannerWrapper>
      <SectionTitle>{t('uooipropertymanagement:Our Portfolio')}</SectionTitle>
    </>
  )
}

export default UooiPropertyManagementProfile
